import BACKEND_AMOUNT_MODIFIER from 'config/backendAmountModifier';
import { compact, flatten, groupBy, includes } from 'lodash';
import { Budget } from 'store/budget/interfaces';
import getPeriodByBudgetPeriodType from './getPeriodByBudgetPeriodType';

export interface TransactionBudgetTableItem {
  id: string;
  name: string;
  type: string;
  description: string,
  period: string;
  targetAmount: string;
  currency: string
}

function calculateTransactionsSums(transactionsInPeriod: Array<TransactionBudgetTableItem | null>) {
    return Object.entries(groupBy(compact(transactionsInPeriod), (transaction) => transaction.period)).map((dataTransactions) => {
        const transactions = dataTransactions[1];
        const totalTargetAmount = transactions.reduce((acc, transaction) => acc + (+transaction.targetAmount), 0);
        return { ...transactions[0], targetAmount: totalTargetAmount.toString() };
    })
}

export default function getBudgetsTransactionsByPeriod(budget: Budget, displayedPeriod: string[]) {
    const budgetPeriodType = budget.period;

    const nestedUnifiedBudgetContractors = budget?.budgetContractors.map(
        (contractor) => {
            const { name, id } = contractor.contractor;

            const transactionsInPeriod = contractor.transactions?.map((transaction) => {
                const period = getPeriodByBudgetPeriodType(transaction, budgetPeriodType);

                if (!includes(displayedPeriod, period)) {
                    return null;
                }

                return {
                    id: `contractor-${id}`,
                    type: 'contractor',
                    name,
                    description: name,
                    period,
                    targetAmount: String(transaction.targetAmount),
                    currency: '',
                };
            })

            return calculateTransactionsSums(transactionsInPeriod);
        },
    );

    const nestedUnifiedBudgetProjects = budget?.budgetProjects.map((project) => {
        const { name, id, } = project.project;

        if (!project.data) {
            return null
        }

        const transactionsInPeriod = Object.entries(project.data).map(([period, targetAmount]) => {
            if (!includes(displayedPeriod, period)) {
                return null;
            }

            return {
                id: `project-${id}`,
                type: 'project',
                name,
                description: name,
                period,
                targetAmount: String(targetAmount),
                currency: '',
            };
        });

        return calculateTransactionsSums(transactionsInPeriod);

    });

    const unifiedBudgetTransactions = budget?.budgetTransactions?.map(
        (transaction) => {
            const { description, targetAmountNet, id, currency } =
                transaction.transaction;

            const transactionTargetAmount = transaction.targetAmount
                ? transaction.targetAmount / BACKEND_AMOUNT_MODIFIER
                : targetAmountNet;

            const period = getPeriodByBudgetPeriodType(transaction.transaction, budgetPeriodType);

            return {
                id: `transaction-${id}`,
                type: 'transaction',
                name: description,
                description,
                period,
                targetAmount: transactionTargetAmount.toString(),
                currency,
            };
        },
    );

    const unifiedTransactions = compact([
        ...flatten(nestedUnifiedBudgetContractors),
        ...flatten(nestedUnifiedBudgetProjects),
        ...unifiedBudgetTransactions,
    ]);

    const groupedTransactions = unifiedTransactions.reduce<Record<string, Record<string, TransactionBudgetTableItem>>>((acc, transaction) => {
        const transactionName = transaction.name;
        if (!acc[transactionName]) {
            acc[transactionName] = {};
        }
        acc[transactionName][transaction.period] = transaction;
        return acc;
    }, {});

    const groupedTransactionTypes = unifiedTransactions.reduce<Record<string, string>>((acc, transaction) => {
        const { name, type } = transaction;
        if (!acc[name]) {
            acc[name] = type;
        }
        return acc;
    }, {});

    return { groupedTransactions, groupedTransactionTypes };
}

